import type { CasinoServiceParameters } from '@ps/web-martech-cms-types';
import produce from 'immer';
import isNil from 'lodash';

import { MarketingTag, PlayType } from '@swf/core/types/CasinoGame';
import type { TrackedTranslation } from '@swf/core/types/i18n';

import { CasinoJoinOperand, CasinoOperand } from '../enums';

import {
  TXTACL_Roulette,
  TXTCLI_Blackjack,
  TXTCLI_New,
  TXTCLI_Other__games,
  TXTCLI_Poker,
  TXTCLI_Popular,
  TXTMOB_Exclusive,
  TXTMOB_Jackpot,
  TXTWCL_Baccarat_and_Sic_Bo,
  TXTWCL_Game_Shows,
  TXTWCL_MegawaysXtradeX,
  TXTWCL_Multiplayer,
  TXTWCL_Recently_Played,
  TXTWCL_Recommended,
  TXTWCL_Scratch_Cards
} from './casinoFilters.messages.json';

export enum Service {
  Games = 'games.all',
  Recent = 'games.recent',
  Recommended = 'games.recommended',
  FreeSpinBonus = 'games.myfsb'
}

export interface Filter {
  name: TrackedTranslation;
  auth?: boolean;
  service: Service;
  makeQuery: (query: CasinoServiceParameters, siteName?: string) => CasinoServiceParameters;
  slug: string;
  checkQuery: GameFilter | undefined;
}

export enum FilterTag {
  Slots = 'Slots',
  Blackjack = 'Blackjack',
  Roulette = 'Roulette',
  Other = 'Other Table',
  Poker = 'Poker',
  Megaways = 'Megaways',
  Vegas = 'Vegas',
  Live = 'Live',
  GameShows = 'Game Shows',
  BaccaratSicBo = 'Baccarat SicBo',
  ScratchCard = 'Scratch Card'
}

export enum Feature {
  Jackpot = 1,
  EvolutionVIP = 2,
  EvoluationFT = 4,
  FreeSpin = 8,
  BonusRounds = 16,
  MGA = 32,
  IGT = 64
}

export enum FilterType {
  None = 'none',
  Slots = 'slots',
  Table = 'table',
  Live = 'live',
  InstantWin = 'instantwin'
}

export enum GameFilter {
  None = 'none',
  SlotsPopular = 'slots_popular',
  SlotsNew = 'slots_new',
  SlotsJackpot = 'slots_jackpot',
  SlotsExclusive = 'slots_exclusive',
  SlotsMegaways = 'slots_megaways',
  LivePopular = 'live_casino_popular',
  LiveRoulette = 'live_casino_roulette',
  LiveBlackjack = 'live_casino_blackjack',
  LiveBacSic = 'live_casino_bac_sic',
  LivePoker = 'live_casino_poker',
  LiveOther = 'live_casino_other',
  LiveGameShow = 'live_casino_game_shows',
  CardPopular = 'card_table_popular',
  CardMulti = 'card_table_multiplayer',
  CardRoulette = 'card_table_roulette',
  CardBlackjack = 'card_table_blackjack',
  CardPoker = 'card_table_poker',
  CardOther = 'card_table_other',
  InstantWinPopular = 'instant_win_popular',
  InstantWinNew = 'instant_win_new',
  InstantWinScratch = 'instant_win_scratch_cards'
}

export interface FilterItem {
  filter: GameFilter;
  tag: TrackedTranslation;
  slug: string;
}

export const filterURL: Record<string, string> = {
  [FilterType.Slots]: '/slots/:slug',
  [FilterType.Table]: '/card-and-table/:slug',
  [FilterType.Live]: '/live/:slug',
  [FilterType.InstantWin]: '/instantwin/:slug'
};

const makeQuery = (query: CasinoServiceParameters): CasinoServiceParameters => query;

const filterTag = (query: CasinoServiceParameters, ...tag: string[]) =>
  produce(query, (draft) => {
    if (!draft.qry) {
      draft.qry = [
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          exclude_query_values: [],
          formalparamcode: 'tagname',
          values: tag,
          operand: CasinoOperand.EQ,
          join: CasinoJoinOperand.AND,
          dynamic: false
        }
      ];
    } else {
      (draft.qry[0].values as string[]).push(...tag);
      draft.qry[0].operand = CasinoOperand.EQ;
    }
  });

// @TODO Deprecated Remove on Filter cleanup.
const tableGameFilters: Filter[] = [
  {
    name: TXTCLI_Popular,
    slug: 'popular',
    service: Service.Games,
    makeQuery,
    checkQuery: GameFilter.CardPopular
  },
  {
    name: TXTWCL_Multiplayer,
    slug: 'multiplayer',
    service: Service.Games,
    checkQuery: GameFilter.CardMulti,
    makeQuery: (query) =>
      produce(query, (draft) => {
        draft.qry.push({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          exclude_query_values: [],
          formalparamcode: 'playtype',
          values: [PlayType.Multi.toString()],
          operand: CasinoOperand.EQ,
          join: CasinoJoinOperand.AND,
          dynamic: false
        });
      })
  },
  {
    name: TXTACL_Roulette,
    slug: 'roulette',
    service: Service.Games,
    checkQuery: GameFilter.CardRoulette,
    makeQuery: (query) => filterTag(query, FilterTag.Roulette)
  },
  {
    name: TXTCLI_Blackjack,
    slug: 'blackjack',
    service: Service.Games,
    checkQuery: GameFilter.CardBlackjack,
    makeQuery: (query) => filterTag(query, FilterTag.Blackjack)
  },
  {
    name: TXTWCL_Baccarat_and_Sic_Bo,
    slug: 'baccarat',
    service: Service.Games,
    checkQuery: GameFilter.LiveBacSic,
    makeQuery: (query) => filterTag(query, FilterTag.Live, FilterTag.BaccaratSicBo)
  },
  {
    name: TXTCLI_Poker,
    slug: 'poker',
    service: Service.Games,
    checkQuery: GameFilter.CardPoker,
    makeQuery: (query) => filterTag(query, FilterTag.Poker)
  },
  {
    name: TXTCLI_Other__games,
    slug: 'other',
    service: Service.Games,
    checkQuery: GameFilter.CardOther,
    makeQuery: (query) => filterTag(query, FilterTag.Other)
  },
  {
    name: TXTWCL_Game_Shows,
    slug: 'game-shows',
    service: Service.Games,
    checkQuery: GameFilter.LiveGameShow,
    makeQuery: (query) => filterTag(query, FilterTag.Live, FilterTag.GameShows)
  }
];

// @TODO Deprecated Remove on Filter cleanup.
const liveGameFilters: Filter[] = [
  {
    name: TXTCLI_Popular,
    slug: 'popular',
    service: Service.Games,
    makeQuery,
    checkQuery: GameFilter.LivePopular
  },
  {
    name: TXTACL_Roulette,
    slug: 'roulette',
    service: Service.Games,
    checkQuery: GameFilter.LiveRoulette,
    makeQuery: (query) => filterTag(query, FilterTag.Roulette)
  },
  {
    name: TXTCLI_Blackjack,
    slug: 'blackjack',
    service: Service.Games,
    checkQuery: GameFilter.LiveBlackjack,
    makeQuery: (query) => filterTag(query, FilterTag.Blackjack)
  },
  {
    name: TXTWCL_Baccarat_and_Sic_Bo,
    slug: 'baccarat',
    service: Service.Games,
    checkQuery: GameFilter.LiveBacSic,
    makeQuery: (query) => filterTag(query, FilterTag.Live, FilterTag.BaccaratSicBo)
  },
  {
    name: TXTCLI_Poker,
    slug: 'poker',
    service: Service.Games,
    checkQuery: GameFilter.LivePoker,
    makeQuery: (query) => filterTag(query, FilterTag.Poker)
  },
  {
    name: TXTCLI_Other__games,
    slug: 'other',
    service: Service.Games,
    checkQuery: GameFilter.LiveOther,
    makeQuery: (query) => filterTag(query, FilterTag.Other)
  },
  {
    name: TXTWCL_Game_Shows,
    slug: 'game-shows',
    service: Service.Games,
    checkQuery: GameFilter.LiveGameShow,
    makeQuery: (query) => filterTag(query, FilterTag.Live, FilterTag.GameShows)
  }
];

// @TODO Deprecated Remove on Filter cleanup.
const slotFilters: Filter[] = [
  {
    name: TXTCLI_Popular,
    slug: 'popular',
    service: Service.Games,
    makeQuery,
    checkQuery: GameFilter.SlotsPopular
  },
  {
    name: TXTCLI_New,
    slug: 'new',
    service: Service.Games,
    checkQuery: GameFilter.SlotsNew,
    makeQuery: (query, siteName) => filterTag(query, `${MarketingTag.New} ${siteName}`)
  },
  {
    name: TXTMOB_Jackpot,
    slug: 'jackpot',
    service: Service.Games,
    checkQuery: GameFilter.SlotsJackpot,
    makeQuery: (query) =>
      produce(query, (draft) => {
        draft.qry.push({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          exclude_query_values: [],
          formalparamcode: 'features',
          values: [Feature.Jackpot.toString()],
          operand: CasinoOperand.BITAND,
          join: CasinoJoinOperand.AND,
          dynamic: false
        });
      })
  },
  {
    name: TXTMOB_Exclusive,
    slug: 'exclusive',
    service: Service.Games,
    checkQuery: GameFilter.SlotsExclusive,
    makeQuery: (query, siteName) => filterTag(query, `${MarketingTag.Exclusive} ${siteName}`)
  },
  {
    name: TXTWCL_MegawaysXtradeX,
    slug: 'megaways',
    service: Service.Games,
    checkQuery: GameFilter.SlotsMegaways,
    makeQuery: (query) => filterTag(query, FilterTag.Megaways)
  },
  {
    name: TXTWCL_Recommended,
    slug: 'recommended',
    service: Service.Recommended,
    checkQuery: undefined,
    makeQuery,
    auth: true
  },
  {
    name: TXTWCL_Recently_Played,
    slug: 'recent',
    service: Service.Recent,
    checkQuery: undefined,
    makeQuery: (query) => filterTag(query, FilterTag.Slots),
    auth: true
  }
];

// @TODO Deprecated Remove on Filter cleanup.
const instantWinFilters: Filter[] = [
  {
    name: TXTCLI_Popular,
    slug: 'popular',
    service: Service.Games,
    makeQuery,
    checkQuery: GameFilter.InstantWinPopular
  },
  {
    name: TXTCLI_New,
    slug: 'new',
    service: Service.Games,
    checkQuery: GameFilter.InstantWinNew,
    makeQuery: (query, siteName) => filterTag(query, `${MarketingTag.New} ${siteName}`)
  },
  {
    name: TXTWCL_Scratch_Cards,
    slug: 'scratch-cards',
    service: Service.Games,
    checkQuery: GameFilter.InstantWinScratch,
    makeQuery: (query) => filterTag(query, FilterTag.ScratchCard)
  }
];

// @TODO Deprecated Remove on Filter cleanup.
export const menus: {
  [key: string]: Filter[];
} = {
  [FilterType.Slots]: slotFilters,
  [FilterType.Table]: tableGameFilters.filter(
    (filter) => filter.name !== TXTWCL_Game_Shows && filter.name !== TXTWCL_Baccarat_and_Sic_Bo
  ),
  [FilterType.Live]: liveGameFilters,
  [FilterType.InstantWin]: instantWinFilters
};

export const findFilterIndex = (tab: string, key: FilterType) =>
  isNil(menus[key]?.findIndex((item) => item.slug === tab)).value();
